import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import {
  Dialog,
  SharedModule,
  DataService,
  NotificationService,
} from "@bb-vendure/admin-ui/core";
import {
  FormBuilder,
  UntypedFormGroup,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { concat, Observable, of, Subject, take } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  FetchShopSuppliersDocument,
  SortOrder,
  FetchRetailersDocument,
  CreatePurchaseRequestManuallyDocument,
  CreateRequestPurchaseNeededInput,
} from "../../../generated-graphql/graphql";
import { ProductSelectorComponent } from "../../../product-selector/product-selector.component";
import { ProductVariantSelectorComponent } from "../../../product-variant-selector/product-variant-selector.component";

export interface Supplier {
  id: string;
  name: string;
}

@Component({
  imports: [
    SharedModule,
    ProductSelectorComponent,
    ProductVariantSelectorComponent,
  ],
  standalone: true,
  selector: "create-request-dialog",
  templateUrl: "./create-request-dialog.component.html",
})
export class CreateRequestDialogComponent implements Dialog<any>, OnInit {
  resolveWith: (result?: any) => void;

  message = "";
  cancellable: boolean = true;
  error = "";
  isLoading = false;
  isLoadingRetailer = false;

  requestList: Map<string, UntypedFormGroup> = new Map();
  requestForm: FormArray;

  detailForm = this.formBuilder.group({
    note: [""],
    expectedDate: [null],
    supplier: [null, Validators.required],
    retailer: [null, Validators.required],
    requests: this.formBuilder.array([]),
    gifts: this.formBuilder.array([]),
    productId: [null, Validators.required],
    isGift: [false],
  });
  today = new Date().toISOString().substring(0, 10);

  suppliers$: Observable<Supplier[]>;
  retailers$: Observable<Supplier[]>;
  input$ = new Subject<string>();
  inputRetailer$ = new Subject<string>();
  selectedSupplier: Supplier;

  requestPurchase: FormArray;

  constructor(
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  variantRequests(groupForm: UntypedFormGroup) {
    return groupForm.get("requests") as FormArray;
  }

  giftRequests(groupForm: UntypedFormGroup) {
    return groupForm.get("gifts") as FormArray;
  }

  ngOnInit(): void {
    this.suppliers$ = concat(
      this.dataService
        .query(FetchShopSuppliersDocument, {
          options: {
            take: 5,
            sort: {
              createdAt: SortOrder.Desc,
            },
          },
        })
        .mapSingle(({ shopSuppliers }) => shopSuppliers?.items ?? []),
      this.input$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          this.dataService
            .query(FetchShopSuppliersDocument, {
              options: {
                filter: {
                  name: {
                    contains: term,
                  },
                },
              },
            })
            .mapStream(({ shopSuppliers }) => shopSuppliers.items)
            .pipe(
              catchError(() => of([])),
              tap(() => (this.isLoading = false))
            )
        )
      )
    );
    this.retailers$ = concat(
      this.dataService
        .query(FetchRetailersDocument, {
          options: {
            take: 5,
            sort: {
              createdAt: SortOrder.Desc,
            },
          },
        })
        .mapSingle(({ retails }) => retails?.items ?? []),
      this.inputRetailer$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          this.dataService
            .query(FetchRetailersDocument, {
              options: {
                filter: {
                  name: {
                    contains: term,
                  },
                },
              },
            })
            .mapStream(({ retails }) => retails.items)
            .pipe(
              catchError(() => of([])),
              tap(() => (this.isLoadingRetailer = false))
            )
        )
      )
    );
  }

  select() {
    const data = this.detailForm.value;

    const requestInputs: CreateRequestPurchaseNeededInput[] = [];

    let date = "";

    for (const r of data.requests! as any[]) {
      requestInputs.push({
        productVariantId: r.id,
        quantityNeeded: r.quantity,
        productId: data.isGift ? data.productId! : undefined,
        notSpecify: r.notSpecify ?? false,
        expectedDate: new Date(r.expectedDate!).toISOString(),
      });

      if (!date) {
        date = new Date(r.expectedDate!).toISOString();
      }
    }

    if (data.gifts && data.gifts.length > 0) {
      for (const r of data.gifts as any[]) {
        requestInputs.push({
          productVariantId: r.id,
          quantityNeeded: r.quantity,
          productId: r.productId,
          notSpecify: r.notSpecify ?? false,
          expectedDate: new Date(r.expectedDate!).toISOString(),
        });
      }
    }

    this.dataService
      .mutate(CreatePurchaseRequestManuallyDocument, {
        input: {
          productId: data.productId!,
          expectedDate: date,
          note: data.note!,
          shopSupplierId: (data.supplier! as any).id!,
          retailerId: (data.retailer! as any).id!,
          requests: requestInputs,
        },
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res.createRequestManual) {
          this.resolveWith(true);
        }
      });
  }

  cancel() {
    this.resolveWith();
  }

  trackByFn(item: Supplier) {
    return item.id;
  }

  onSelectProduct(event: any) {
    if (event) {
      //console.log("product", event);

      this.variantRequests(this.detailForm).clear();
      this.giftRequests(this.detailForm).clear();
      this.detailForm.patchValue({
        productId: event.id,
        isGift: event.customFields.freeGifts ?? false,
        supplier: event.customFields.supplier ?? null,
        retailer: event.customFields.retailer ?? null,
      });

      // TODO: add retailer and supplier
    } else {
      this.detailForm.patchValue({
        productId: null,
        isGift: null,
        supplier: null,
        retailer: null,
      });

      // TODO: removes retailer and supplier

      this.variantRequests(this.detailForm).clear();
      this.giftRequests(this.detailForm).clear();
    }
  }

  onSelectProductVariant(event: any) {
    const existed = this.detailForm.value.requests?.find(
      (r: any) => r.id === event.id
    );

    if (!existed) {
      this.variantRequests(this.detailForm).push(
        this.formBuilder.group({
          id: event.id,
          productVariant: new FormControl(event.name),
          sku: new FormControl(event.sku),
          orderQuantity: new FormControl(event.stockAllocated),
          stockOnHand: new FormControl(event.stockOnHand),
          quantity: [0, Validators.min(0)],
          notSpecify: [false],
          expectedDate: [null, Validators.required],
        })
      );

      //this.cdr.markForCheck();
    }
  }

  removeProductVariant(id: string) {
    const index = this.detailForm.value.requests!.findIndex(
      (r: any) => r.id === id
    );

    this.variantRequests(this.detailForm).removeAt(index);
  }

  removeGift(index: number) {
    this.giftRequests(this.detailForm).removeAt(index);
  }

  addGift() {
    this.giftRequests(this.detailForm).push(
      this.formBuilder.group({
        id: [null, Validators.required],
        productVariant: ["", Validators.required],
        productId: [null],
        sku: ["", Validators.required],
        orderQuantity: [0],
        stockOnHand: [0],
        quantity: [0, Validators.min(0)],
        notSpecify: [false],
        expectedDate: [null, Validators.required],
      })
    );
  }

  addGiftRequestForm(event: any, form: FormGroup) {
    const existed = this.detailForm.value.gifts?.find(
      (r: any) => r.id === event.id
    );

    if (existed) {
      this.notificationService.error("This gift is already added");
      return;
    }

    form.patchValue({
      id: event.id,
      productVariant: event.name,
      sku: event.sku,
      orderQuantity: event.stockAllocated,
      stockOnHand: event.stockOnHand,
      productId: this.detailForm.value.productId!,
    });
  }
}
